/* eslint-disable */

import Vue from "vue";
import createAuth0Client from "@auth0/auth0-spa-js";
import axios from 'axios';
import store from '../store';
import router from '../router';

const DEFAULT_REDIRECT_CALLBACK = () => {
  window.history.replaceState({}, document.title, window.location.pathname);
};

let instance;

export const getInstance = () => instance;

export const useAuth0 = ({
  onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
  redirectUri = window.location.origin,
  ...options
}) => {
  if (instance) return instance;

  instance = new Vue({
    data() {
      return {
        loading: true,
        isAuthenticated: false,
        user: {},
        auth0Client: null,
        popupOpen: false,
        error: null,
        tokenExpiryInterval: null,
        tokenExpiryWarningShown: false,
        lastActivity: null,
        activityTimeout: null,
        activityListeners: []
      };
    },
    methods: {
      async loginWithPopup(o) {
        this.popupOpen = true;
        try {
          await this.auth0Client.loginWithPopup(o);
        } catch (e) {
          console.error(e);
        } finally {
          this.popupOpen = false;
        }
        this.user = await this.auth0Client.getUser();
        this.isAuthenticated = true;
        this.startTokenExpiryMonitoring();
        this.startActivityMonitoring();
      },
      async handleRedirectCallback() {
        this.loading = true;
        try {
          await this.auth0Client.handleRedirectCallback();
          this.user = await this.auth0Client.getUser();
          this.isAuthenticated = true;
          this.startTokenExpiryMonitoring();
          this.startActivityMonitoring();
        } catch (e) {
          this.error = e;
        } finally {
          this.loading = false;
        }
      },
      loginWithRedirect(o) {
        return this.auth0Client.loginWithRedirect(o);
      },
      getIdTokenClaims(o) {
        return this.auth0Client.getIdTokenClaims(o);
      },
      getTokenSilently(o) {
        return this.auth0Client.getTokenSilently(o);
      },
      getTokenWithPopup(o) {
        return this.auth0Client.getTokenWithPopup(o);
      },
      logout(o) {
        this.stopTokenExpiryMonitoring();
        this.stopActivityMonitoring();
        this.tokenExpiryWarningShown = false;
        store.commit('setSpr2Profile', null);
        store.commit('setUserCompanies', []);
        store.commit('setUserCurrentCompany', null);
        store.commit('setLoggedInUserPermissions', []);
        localStorage.removeItem('currentCompanyId');
        return this.auth0Client.logout(o);
      },
      onUserActivity() {
        this.lastActivity = Date.now();
        
        // Debounce activity tracking - only update every 30 seconds max
        if (this.activityTimeout) {
          clearTimeout(this.activityTimeout);
        }
        
        this.activityTimeout = setTimeout(() => {
          // Check if we should refresh token due to activity
          this.checkTokenRefreshOnActivity();
        }, 1000); // Debounce for 1 second
      },
      async checkTokenRefreshOnActivity() {
        try {
          // Use access token for consistency with checkTokenExpiry
          const accessToken = await this.getTokenSilently();
          if (!accessToken) return;

          // Decode the access token to get expiry
          const tokenParts = accessToken.split('.');
          if (tokenParts.length !== 3) return;
          
          const payload = JSON.parse(atob(tokenParts[1]));
          if (!payload.exp) return;

          const now = Math.floor(Date.now() / 1000);
          const timeUntilExpiry = payload.exp - now;
          
          // If user has been active in last 5 minutes and token expires in next 10 minutes, refresh it
          const timeSinceActivity = Date.now() - this.lastActivity;
          const fiveMinutesInMs = 5 * 60 * 1000;
          const tenMinutesInSeconds = 10 * 60;
          
          if (timeSinceActivity < fiveMinutesInMs && timeUntilExpiry < tenMinutesInSeconds && timeUntilExpiry > 0) {
            console.log('Refreshing token due to user activity and approaching expiry');
            await this.refreshTokenSilently();
          }
        } catch (error) {
          console.error('Error checking token refresh on activity:', error);
        }
      },
      async refreshTokenSilently() {
        try {
          // Get a fresh token silently
          await this.getTokenSilently({ 
            ignoreCache: true,
            timeoutInSeconds: 10
          });
          
          // Reset warning flag since we have a fresh token
          this.tokenExpiryWarningShown = false;
          
          console.log('Token refreshed silently due to user activity');
        } catch (error) {
          console.error('Error refreshing token silently:', error);
          // If silent refresh fails, the normal expiry flow will handle it
        }
      },
      startActivityMonitoring() {
        const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
        
        // Remove existing listeners first
        this.stopActivityMonitoring();
        
        // Add event listeners
        events.forEach(event => {
          const listener = () => this.onUserActivity();
          document.addEventListener(event, listener, true);
          this.activityListeners.push({ event, listener });
        });
        
        // Initialize last activity
        this.lastActivity = Date.now();
        
        console.log('Started activity monitoring for token refresh');
      },
      stopActivityMonitoring() {
        // Remove all activity listeners
        this.activityListeners.forEach(({ event, listener }) => {
          document.removeEventListener(event, listener, true);
        });
        this.activityListeners = [];
        
        // Clear activity timeout
        if (this.activityTimeout) {
          clearTimeout(this.activityTimeout);
          this.activityTimeout = null;
        }
        
        console.log('Stopped activity monitoring');
      },
      async checkTokenExpiry() {
        try {
          // Use access token for expiry checking to be consistent with refresh logic
          const accessToken = await this.getTokenSilently();
          if (!accessToken) {
            console.log('No access token available, logging out');
            await this.logout({ returnTo: window.location.origin });
            return;
          }

          // Decode the access token to get expiry
          const tokenParts = accessToken.split('.');
          if (tokenParts.length !== 3) {
            console.log('Invalid access token format, logging out');
            await this.logout({ returnTo: window.location.origin });
            return;
          }
          
          const payload = JSON.parse(atob(tokenParts[1]));
          if (!payload.exp) {
            console.log('No expiry in access token, logging out');
            await this.logout({ returnTo: window.location.origin });
            return;
          }

          const now = Math.floor(Date.now() / 1000);
          const timeUntilExpiry = payload.exp - now;

          console.log(`Token expires in ${timeUntilExpiry} seconds`);

          // Show warning 5 minutes before expiry (only if user hasn't been active recently)
          const timeSinceActivity = Date.now() - (this.lastActivity || 0);
          const fiveMinutesInMs = 5 * 60 * 1000;
          
          if (timeUntilExpiry <= 300 && timeUntilExpiry > 0 && !this.tokenExpiryWarningShown) {
            // Only show warning if user hasn't been active recently (activity-based refresh would handle it)
            if (timeSinceActivity > fiveMinutesInMs) {
              this.tokenExpiryWarningShown = true;
              const minutes = Math.floor(timeUntilExpiry / 60);
              console.log(`Showing expiry warning: ${minutes} minutes remaining`);
              alert(`Your session will expire in ${minutes} minute(s). Please save your work.`);
            } else {
              console.log('User has been active recently, skipping warning (activity-based refresh will handle)');
            }
          }

          // Auto-logout if token has expired (with 30 second grace period for network delays)
          if (timeUntilExpiry <= -30) {
            console.log('Token has expired beyond grace period, logging out user');
            alert('Your session has expired. You will be redirected to the login page.');
            await this.logout({
              returnTo: window.location.origin
            });
          }
        } catch (error) {
          console.error('Error checking token expiry:', error);
          // If we can't get or decode the token, assume it's invalid and logout
          if (this.isAuthenticated) {
            console.log('Unable to get or decode access token, logging out user');
            await this.logout({
              returnTo: window.location.origin
            });
          }
        }
      },
      startTokenExpiryMonitoring() {
        // Check every 30 seconds
        this.tokenExpiryInterval = setInterval(() => {
          this.checkTokenExpiry();
        }, 30000);
        
        // Also check immediately
        this.checkTokenExpiry();
      },
      stopTokenExpiryMonitoring() {
        if (this.tokenExpiryInterval) {
          clearInterval(this.tokenExpiryInterval);
          this.tokenExpiryInterval = null;
        }
      },
      async loadCompanyPermissionsForUser(companyId) {
        return axios.get(`/api/company/${companyId}/loggedInUserPermissions`)
          .then(result => store.commit('setLoggedInUserPermissions', result.data))
          .catch(e => console.log(`Error loading permissions: ${e}`));
      },
      async getPersonByEmail(companyId, emailAddress) {
        return axios.get(`/api/company/${companyId}/people?showArchived=false&filterByAssociations=false`)
          .then(result => result.data.people.find(p => p.emailAddress === emailAddress))
          .catch(e => console.log(`Error getting person: ${e}`));
      },
      async refreshCompanyPermissionsForUser() {
        const companyId = store.state.userCurrentCompany.companyId;
        return this.loadCompanyPermissionsForUser(companyId);
      },
      async setUserSelectedComany(companyId) {
        const companies = store.state.userCompanies;
        const selected = companies.find(p => p.companyId === companyId);
        if (selected) {
          store.commit('setUserCurrentCompany', selected);
          localStorage.setItem('currentCompanyId', companyId);
          await this.loadCompanyPermissionsForUser(companyId);
          const person = await this.getPersonByEmail(companyId, this.user.email);
          if (person) {
            store.commit('setCompanyPerson', person);
          }
          await store.dispatch('featureFlags/fetchFlags', { companyId });
          store.commit('setCompanyModalVisible', false); // <-- ensure modal closes
        }
      },
      async initializeSpr2User(user) {
        if (!user) return null;
        try {
          const result = await axios.post('/api/users/handlelogin', {
            id: user.sub,
            email: user.email
          });

          const companies = result.data.companies;
          store.commit('setUserCompanies', companies);

          if (companies.length === 1) {
            store.commit('setUserCurrentCompany', companies[0]);
          } else if (companies.length > 1) {
            const selectedCompanyId = localStorage.getItem('currentCompanyId');
            const selected = companies.find(c => c.companyId === selectedCompanyId) || companies[0];
            store.commit('setUserCurrentCompany', selected);
          }

          if (store.state.userCurrentCompany) {
            const companyId = store.state.userCurrentCompany.companyId;
            await this.loadCompanyPermissionsForUser(companyId);
            const person = await this.getPersonByEmail(companyId, user.email);
            store.commit('setCompanyPerson', person);
            store.dispatch('featureFlags/fetchFlags', { companyId });

            pendo.initialize({
              visitor: {
                id: person.id,
                name: person.firstName,
                email: person.emailAddress,
                full_name: `${person.firstName} ${person.lastName}`,
                role: person.positionName
              },
              account: {
                id: companyId,
                name: store.state.userCurrentCompany.companyName,
                environment: process.env.NODE_ENV
              }
            });
          }

          return result.data;
        } catch (e) {
          console.log(`Error in initializeSpr2User: ${e}`);
          return null;
        }
      }
    },
    async created() {
      this.auth0Client = await createAuth0Client({
        domain: options.domain,
        client_id: options.clientId,
        audience: options.audience,
        redirect_uri: redirectUri,
        cacheLocation: 'localstorage',
        useRefreshTokens: true
      });

      try {
        const isReturningFromAuth = window.location.search.includes('code=') &&
                                    window.location.search.includes('state=');

        if (isReturningFromAuth) {
          const { appState } = await this.auth0Client.handleRedirectCallback();
          onRedirectCallback(appState);
        } else {
          this.isAuthenticated = await this.auth0Client.isAuthenticated();
        }
      } catch (e) {
        console.log(`Redirect error: ${e}`);
        this.error = e;
      }

      if (this.isAuthenticated) {
        this.user = await this.auth0Client.getUser();
        const spr2Profile = await this.initializeSpr2User(this.user);
        store.commit('setSpr2Profile', spr2Profile);
        this.startTokenExpiryMonitoring();
        this.startActivityMonitoring();
        // Only redirect to root if we're on the callback page
        if (window.location.pathname === '/callback') {
          router.push('/');
        }
      } else {
        try {
          await this.auth0Client.getTokenSilently();
          this.isAuthenticated = true;
          this.user = await this.auth0Client.getUser();
          const spr2Profile = await this.initializeSpr2User(this.user);
          store.commit('setSpr2Profile', spr2Profile);
          this.startTokenExpiryMonitoring();
          this.startActivityMonitoring();
          // Only redirect to root if we're on the callback page
          if (window.location.pathname === '/callback') {
            router.push('/');
          }
        } catch (e) {
          console.log('Silent login failed, redirecting...');
          // Do not redirect to login for unauthenticated public share routes —
          // /share/:companyId/collection/:shareId and /share/:companyId/:token
          // are intentionally accessible without a Response Grid account.
          const isPublicShareRoute = window.location.pathname.startsWith('/share/');
          if (!isPublicShareRoute) {
            await this.loginWithRedirect();
          }
        }
      }

      this.loading = false;
    },
    beforeDestroy() {
      this.stopTokenExpiryMonitoring();
      this.stopActivityMonitoring();
    },
    mounted() {
      // Clean up monitoring if the browser is being closed or refreshed
      window.addEventListener('beforeunload', () => {
        this.stopTokenExpiryMonitoring();
        this.stopActivityMonitoring();
      });
    }
  });

  return instance;
};

export const Auth0Plugin = {
  install(Vue, options) {
    Vue.prototype.$auth = useAuth0(options);
  }
};



// WEBPACK FOOTER //
// ./src/auth/index.js